/** Shared by the browser, prerenderer, and sitemap generator. */
export const SITE_URL = 'https://somatiq.ai';
export const SOCIAL_IMAGE = '/logo/footer-logo.png';

export interface PageSEO {
  title: string;
  description: string;
  label: string;
  noindex?: boolean;
  type?: 'AboutPage' | 'ContactPage';
}

export const seoPages: Record<string, PageSEO> = {
  '/deployment': {
    title: 'Somatiq PACS – Deployment options',
    description: 'Explore cloud, hybrid and on-premises Somatiq PACS deployment, hardware requirements and rollout steps for your hospital.',
    label: 'Deployment',
    noindex: true,
  },
  '/': {
    title: 'RIS-PACS Software for Radiology in India | Somatiq',
    description: 'Unify radiology workflows with Somatiq RIS-PACS software. Connect registration, medical imaging and reporting for hospitals and diagnostic centres in India.',
    label: 'Home',
  },
  '/portfolio/radone': {
    title: 'RadOne: AI Radiology Reporting Software | Somatiq',
    description: 'Meet RadOne, an AI reporting workspace built for radiologists. Organize findings, create structured reports and review your work before final sign-off.',
    label: 'RadOne',
  },
  '/teleradiology': {
    title: 'Teleradiology Services in India | Somatiq',
    description: 'Explore Somatiq teleradiology services for hospitals and imaging centres in India, with subspecialist reporting, workflow integration and support for your team.',
    label: 'Teleradiology',
  },
  '/about': {
    title: 'About Somatiq | Radiologists Building Radiology Software',
    description: 'Meet the radiologists and technologists behind Somatiq. Learn why we are building unified RIS-PACS software and intelligent tools for medical imaging.',
    label: 'About Somatiq',
    type: 'AboutPage',
  },
  '/careers': {
    title: 'Careers in Radiology Technology | Somatiq',
    description: 'Explore careers at Somatiq and help build the future of radiology technology. Meet our culture and contact our team about opportunities in healthcare software.',
    label: 'Careers',
  },
  '/book-a-demo': {
    title: 'Book a Radiology Software Demo | Somatiq RIS-PACS',
    description: 'Book a personalized Somatiq demo to explore RIS-PACS workflows and radiology reporting tools. Tell us about your hospital, imaging centre or radiology team.',
    label: 'Book a Demo',
    type: 'ContactPage',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Somatiq',
    description: 'Read the Somatiq privacy policy for information about personal data collection, use, retention and contact details for privacy-related questions.',
    label: 'Privacy Policy',
  },
  '/terms-and-conditions': {
    title: 'Terms and Conditions | Somatiq',
    description: 'Read the terms and conditions for using the Somatiq website, including intellectual property, limitations of liability and contact information.',
    label: 'Terms and Conditions',
  },
  // Remove noindex only when substantive, approved content replaces the placeholder.
  '/product': {
    title: 'RIS-PACS Platform Preview | Somatiq',
    description: 'A preview of the Somatiq unified RIS-PACS platform. Detailed product information is coming soon; explore our homepage or contact us for a demonstration.',
    label: 'Product',
    noindex: true,
  },
  '/portfolio/radlinq': {
    title: 'RadLinQ Teleradiology Software Preview | Somatiq',
    description: 'RadLinQ is Somatiq’s teleradiology software for imaging workflows and report delivery. Product details are coming soon; contact us to learn more.',
    label: 'RadLinQ',
    noindex: true,
  },
  '/portfolio/invoiceiq': {
    title: 'InvoiceIQ Healthcare Billing Preview | Somatiq',
    description: 'Preview InvoiceIQ, Somatiq’s healthcare billing and invoicing solution. Detailed product information is coming soon; contact our team to learn more.',
    label: 'InvoiceIQ',
    noindex: true,
  },
  '/blog': {
    title: 'Radiology Technology Blog: Coming Soon | Somatiq',
    description: 'The Somatiq blog is coming soon, with planned insights on radiology workflows, medical imaging and healthcare technology. Explore our platform today.',
    label: 'Blog',
    noindex: true,
  },
  '/404': {
    title: 'Page Not Found | Somatiq',
    description: 'This page could not be found. Visit Somatiq to explore radiology software, AI reporting tools and teleradiology services.',
    label: 'Page Not Found',
    noindex: true,
  },
};

export function structuredData(path: string, page: PageSEO) {
  const url = `${SITE_URL}${path}`;
  const organization = {
    '@type': 'Organization', '@id': `${SITE_URL}/#organization`,
    name: 'Somatiq', legalName: 'Somatiq AI Labs Private Limited', url: `${SITE_URL}/`,
    logo: `${SITE_URL}/logo/footer-logo.png`,
    email: 'contact@somatiq.ai',
    address: { '@type': 'PostalAddress', addressLocality: 'Bengaluru', addressCountry: 'IN' },
    sameAs: ['https://www.linkedin.com/company/somatiqai/'],
  };
  const graph: Record<string, unknown>[] = [organization, {
    '@type': 'WebSite', '@id': `${SITE_URL}/#website`,
    name: 'Somatiq', url: `${SITE_URL}/`, inLanguage: 'en',
    publisher: { '@id': organization['@id'] },
  }, {
    '@type': page.type || 'WebPage', '@id': `${url}#webpage`,
    url, name: page.title, description: page.description, inLanguage: 'en',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    ...(path !== '/' && !page.noindex ? { breadcrumb: { '@id': `${url}#breadcrumb` } } : {}),
  }];
  if (path !== '/' && !page.noindex) {
    graph.push({
      '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: page.label, item: url },
      ],
    });
  }
  if (path === '/' || path === '/portfolio/radone') {
    graph.push({
      '@type': 'SoftwareApplication', '@id': `${url}#software`,
      name: path === '/' ? 'Somatiq RIS-PACS' : 'RadOne',
      url, description: page.description,
      applicationCategory: 'HealthApplication', operatingSystem: 'Web',
      provider: { '@id': organization['@id'] },
      // Do not invent prices or ratings to qualify for rich results.
    });
  }
  if (path === '/teleradiology') {
    graph.push({
      '@type': 'Service', '@id': `${url}#service`, name: 'Somatiq Teleradiology Services',
      serviceType: 'Teleradiology', url, description: page.description,
      provider: { '@id': organization['@id'] },
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}
